import { useState } from 'react'
import type { JSX } from 'react'

import { useSplit } from '@buffer-mono/features'
import {
  CheckIcon,
  Flex,
  Text,
  Heading,
  Paragraph,
  Button,
  toast,
} from '@buffer-mono/popcorn'

import {
  useCanStartTrial,
  useIsLegacyPlan,
  useCurrentOrganization,
  useSubscription,
} from '~publish/contexts/AccountContext'
import { useCta } from '~publish/hooks/useCta'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { useStartTrial } from '~publish/hooks/useStartTrial'
import { navigateToBilling } from '~publish/helpers/navigateToBilling'

import { ApprovalsUpgradePathIllustration } from './ApprovalsUpgradePathIllustration'

import styles from './ApprovalsUpgradePath.module.css'

const ctaKey = 'publish-postTabs-approvals-upgrade-2'

export const ApprovalsUpgradePath = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const canStartTrial = useCanStartTrial()
  const isLegacyPlan = useIsLegacyPlan()
  const currentOrganization = useCurrentOrganization()
  const subscription = useSubscription()
  const { split: featureTrialSplit, isReady } = useSplit(
    'geid-feature-specific-trial',
  )

  const currentOrganizationId = currentOrganization?.id
  const planId = subscription?.plan?.id
  const planInterval = subscription?.interval

  const upgradePathName = canStartTrial
    ? 'approvals-trial'
    : 'approvals-upgrade'
  const cta = useCta(ctaKey, {
    upgradePathName,
  })
  const openGlobalModal = useOpenGlobalModal()

  const startTrial = useStartTrial()

  const handleStartTrial = async (): Promise<void> => {
    const dataCheckForTrial = currentOrganizationId && planId && planInterval
    if (!dataCheckForTrial) return

    try {
      setIsLoading(true)
      await startTrial({
        organizationId: currentOrganizationId,
        attribution: {
          cta: ctaKey,
          upgradePathName,
        },
      })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'An error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClick = (): void => {
    cta.clicked()

    if (isLegacyPlan) {
      navigateToBilling(ctaKey)
      return
    }

    const isInFeatureTrialSplit =
      isReady && featureTrialSplit?.treatment === 'on'

    if (canStartTrial && isInFeatureTrialSplit) {
      openGlobalModal(MODALS.featureTrial, {
        feature: 'approvals',
        cta: ctaKey,
        upgradePathName,
      })
    } else if (canStartTrial) {
      void handleStartTrial()
    } else {
      openGlobalModal(MODALS.planSelector, {
        cta: ctaKey,
        upgradePathName,
      })
    }
  }

  return (
    <div className={styles.container}>
      <Flex direction="column" gap="md">
        <Heading size="large" as="h2">
          Collaboration made easy
        </Heading>
        <Paragraph>
          Say goodbye to the hassle of managing multiple social media channels
          with multiple team mates. With our collaboration features, you can:
        </Paragraph>
        {/* TODO: create List component in popcorn */}
        <ul className={styles.list}>
          <li>
            <Text icon={<CheckIcon color="brand" aria-hidden />}>
              Choose who can post on each of your social media channels
            </Text>
          </li>
          <li>
            <Text icon={<CheckIcon color="brand" aria-hidden />}>
              Review posts for quality and brand before hitting publish
            </Text>
          </li>
          <li>
            <Text icon={<CheckIcon color="brand" aria-hidden />}>
              Collaborate on ideas
            </Text>
          </li>
          <li>
            <Text icon={<CheckIcon color="brand" aria-hidden />}>
              Stay on top of performance with automated reports
            </Text>
          </li>
        </ul>
        {canStartTrial ? (
          <Button size="medium" onClick={handleClick} loading={isLoading}>
            Start a Free Trial
          </Button>
        ) : (
          <Button size="medium" onClick={handleClick}>
            Discover the Team Plan
          </Button>
        )}
      </Flex>
      <Flex direction="column" gap="md">
        <ApprovalsUpgradePathIllustration />
      </Flex>
    </div>
  )
}
